import * as React from 'react';
import * as styles from '../Messages.css';
import { inject, observer } from 'mobx-react';
import { MessagesStore } from '../stores';
import { IAButton } from '../../button/Button';
import { IAIcon } from '../../icon/Icon';
import { FilterPanel } from '../panels/FilterPanel';
import DateTimeService from '../../../services/DateTimeService';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';
import "../../../styles/BasicIA.css";
import MessageService from '../../../services/MessageService';
import { IASmallMenu, SmallMenuItem } from '../../smallMenu/smallMenu';
import { ENTER_KEY, SPACEBAR_KEY } from '../../../global/Constants';
import { IAAddMessageDropdown } from './AddMessageDropdown';
import { IATextField } from '../../textField/TextField';
import { IMessage } from '../../../interfaces/IMessage';
import { IApprover } from '../../../interfaces/IApprover';
import { IAEditableTitle, IEditableTitleChanges } from '../../editableTitle/EditableTitle';

export interface IHeaderProps {
  getMessages: () => void;
  close?: () => void;
  messagesStore?: MessagesStore;
  onTitleSettingsChange?: (changes: IEditableTitleChanges) => void;
}

export interface IHeaderState {
  clearFiltersKey: string;
  isSearching: boolean;
  isMenuOpen: boolean;
  showAddMessageDropDown: boolean;
  searchText: string;
}

@inject('messagesStore')
@observer
export class Header extends React.Component<IHeaderProps, IHeaderState> {

  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private dropDownRef: HTMLDivElement;
  private searchQueryDelay: NodeJS.Timeout;
  private scrollLockToken = 0;

  constructor(props: IHeaderProps) {
    super(props);
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());

    this.state = {
      clearFiltersKey: Helper.getRandomStringKey(),
      isSearching: false,
      isMenuOpen: false,
      showAddMessageDropDown: false,
      searchText: ""
    };
  }

  public componentWillUnmount(): void {
    this.scrollLockToken++;
  }

  // iOS auto-scrolls the nearest scrollable ancestors to bring a focused input into
  // view, which jumps the page when this header isn't at the top of it - undo that
  // for a few frames while the keyboard animates in, without blocking the focus itself.
  private preventIosScrollJumpOnFocus(): void {
    if (window.platform !== "ios" || !(document.activeElement instanceof HTMLElement)) {
      return;
    }
    const token = ++this.scrollLockToken;
    const scrollableAncestors: { element: Element; scrollTop: number }[] = [];
    let node = document.activeElement.parentElement;
    while (node) {
      if (node.scrollHeight > node.clientHeight) {
        scrollableAncestors.push({ element: node, scrollTop: node.scrollTop });
      }
      node = node.parentElement;
    }
    const initialWindowScrollY = window.scrollY;

    let frames = 0;
    const restoreScrollPositions = () => {
      if (token !== this.scrollLockToken) {
        return;
      }
      scrollableAncestors.forEach(({ element, scrollTop }) => {
        if (element.scrollTop !== scrollTop) {
          element.scrollTop = scrollTop;
        }
      });
      if (window.scrollY !== initialWindowScrollY) {
        window.scrollTo(window.scrollX, initialWindowScrollY);
      }
      frames++;
      if (frames < 30) {
        requestAnimationFrame(restoreScrollPositions);
      }
    };
    requestAnimationFrame(restoreScrollPositions);
  }

  private getAmountOfTemplates(): number {
    let amount = 0;
    if (!this.props.messagesStore.instance?.settings?.disableAddPost) {
      amount++;
    }
    if (!this.props.messagesStore.instance?.settings?.disableAddNewsLink) {
      amount++;
    }
    if (this.props.messagesStore.availableTemplates?.length > 0) {
      amount += this.props.messagesStore.availableTemplates.length;
    }
    return amount;
  }

  private addButtonClicked() {
    if (this.getAmountOfTemplates() === 1) {
      if (!this.props.messagesStore.instance?.settings?.disableAddPost) {
        this.addNewMessage("message");
        return;
      }
      if (this.props.messagesStore.component === "Messages" && !this.props.messagesStore.instance?.settings?.disableAddNewsLink) {
        this.addNewMessage("newsLink");
        return;
      }
      if (this.props.messagesStore.availableTemplates?.length > 0) {
        const template = this.props.messagesStore.availableTemplates[0];
        this.addNewMessage("template", template.jsonTemplate, template.title);
        return;
      }
    }
    this.setState({ showAddMessageDropDown: !this.state.showAddMessageDropDown });
  }

  private async addNewMessage(messageType: IMessageType, jsonTemplate?: string, templateName?: string) {
    if (!this.props.messagesStore.preview) {
      let endDate = this.props.messagesStore.component === 'Messages'
        ? this.props.messagesStore.getMessageEndDate()
        : this.dateTimeService.getDatePlusOneHour(new Date());

      if (messageType !== "template") {
        this.props.messagesStore.messageToEdit = {
          title: undefined,
          content: undefined,
          startDate: new Date(),
          endDate: endDate,
          contacts: undefined,
          iaTargetingOperator: undefined,
          iaTargetingTargetContent: false,
          categories: undefined,
          image: undefined,
          iaConfiguration: {
            isNewsLink: messageType === "newsLink",
            newsLink: "",
            openNewsLinkInNewTab: true
          }
        };

        if (this.props.messagesStore.instance?.approvalEnabled && this.props.messagesStore.instance.approvers?.length > 0) {
          this.props.messagesStore.messageToEdit.approvers = this.props.messagesStore.instance.approvers as IApprover[];
        }

        this.props.messagesStore.isCreatingFromTemplate = false;
      } else if (messageType === "template" && jsonTemplate) {
        this.props.messagesStore.isCreatingFromTemplate = true;
        const template = JSON.parse(jsonTemplate) as IMessage;
        if (template.iaConfiguration) {
          template.iaConfiguration.templateName = templateName;
        } else {
          template.iaConfiguration = {
            templateName: templateName
          };
        }

        if (this.props.messagesStore.instance?.approvalEnabled && this.props.messagesStore.instance.approvers?.length > 0) {
          template.approvers = this.props.messagesStore.instance.approvers as IApprover[];
        }

        this.props.messagesStore.messageToEdit = await this.props.messagesStore.setTemplateProperties(template, endDate);
      }

      if (this.props.messagesStore.creatingNewMessage &&
        this.props.messagesStore?.messageToEdit.endDate &&
        this.props.messagesStore?.instance?.settings.shouldByDefaultExpireAfterAmountOfDays != undefined) {
        let defaultEndDate = new Date();
        defaultEndDate.setDate(defaultEndDate.getDate() + this.props.messagesStore.instance.settings.shouldByDefaultExpireAfterAmountOfDays);
        this.props.messagesStore.endDate = new Date(defaultEndDate.setHours(0, 0, 0, 0));
      }

      this.props.messagesStore.messageToEditOriginal = Object.assign(
        {},
        this.props.messagesStore.messageToEdit
      );
      this.props.messagesStore.creatingNewMessage = true;
    }
  }

  private toggleFiltering() {
    if (!this.props.messagesStore.preview) {
      this.props.messagesStore.showFilterPanel = true;
    }
  }

  private clearFilters() {
    if (!this.props.messagesStore.preview) {
      this.props.messagesStore.updateFilter({
        isFiltered: false,
        showOnlyAuthorMessages: 'All',
        showPeriodMessage: 'All time',
        selectedPeolple: [],
        selectedCategories: this.props.messagesStore.selectedCategories
          ? this.props.messagesStore.selectedCategories
          : [],
        startDate: undefined,
        endDate: undefined,
        selectedTypesOfContent: [],
        onlyShowPublished: false,
        onlyShowPublishedToFeed: false,
      });
      this.setState({ clearFiltersKey: Helper.getRandomStringKey() });
      this.props.messagesStore.messages = [];
      this.props.messagesStore.pageNumber = 1;
      this.props.getMessages();
    }
  }

  private async markAllAsRead() {
    await MessageService.readAllMessages(this.props.messagesStore.environment, this.props.messagesStore.token, this.props.messagesStore.tenant, this.props.messagesStore.instance.instanceName).then(() => {
      this.props.getMessages();
    })
  }

  public onMenuItemClick(itemTitle: string) {
    this.setState({ isMenuOpen: false })
    switch (itemTitle) {
      case 'AddNewMessage':
        this.addNewMessage("message");
        break;
      case 'AddNewsLink':
        this.addNewMessage("newsLink");
        break;
      case "ShowPinnedSortPanel":
        this.props.messagesStore.showSortPinnedPanel = true;
        break;
      case 'Filter':
        this.toggleFiltering();
        break;
      case 'ClearFilter':
        this.clearFilters();
        break;
      case 'MarkAllAsRead':
        this.markAllAsRead();
        break;
      case 'showAll':
        window.open(this.props.messagesStore.showAllButtonLink);
        break;
      default:
        if (this.props.messagesStore.availableTemplates?.length > 0) {
          const template = this.props.messagesStore.availableTemplates.find(template => template.id === itemTitle);
          if (template) {
            this.addNewMessage("template", template.jsonTemplate);
          }
        }
        break;
    }
  }

  private searchMessages() {
    const hasSearchQuery = !!this.state.searchText;
    this.props.messagesStore.isSearching = hasSearchQuery;
    this.props.messagesStore.loading = true;
    this.props.messagesStore.searchText = encodeURIComponent(this.state.searchText);
    this.props.messagesStore.messages = [];
    this.props.messagesStore.pageNumber = 1;
    this.props.getMessages();
  }

  private getSearchField(expandWidth: boolean): JSX.Element {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: this.props.messagesStore.isMobile ? "justify" : "end"
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            maxWidth: expandWidth ? undefined : 250,
            marginBottom: this.props.messagesStore.collapseCommandBarItems ? 13 : undefined
          }}
        >
          <IATextField
            text={this.state.searchText}
            onChange={(searchText) => {
              this.setState({ searchText });
              clearTimeout(this.searchQueryDelay);
              this.searchQueryDelay = setTimeout(() => {
                this.searchMessages();
              }, 500);
            }}
            borderRadius={5}
            highlightColor={this.props.messagesStore.hasDarkBackground ? "#fff" : this.props.messagesStore.highlightColor}
            placeholder={this.localizationService.strings.Messages_Search}
            style={{
              marginTop: 0,
            }}
            placeholderColor={this.props.messagesStore.hasDarkBackground ? "#ffffffb3" : undefined}
            inputFieldStyle={{
              marginTop: 0,
              height: this.props.messagesStore.isMobile ? 40 : 24,
              lineHeight: this.props.messagesStore.isMobile ? 29 : 12,
              paddingLeft: this.props.messagesStore.isMobile ? 10 : 5,
              paddingBottom: this.props.messagesStore.isMobile ? 0 : 2,
              backgroundColor: this.props.messagesStore.hasDarkBackground ? "transparent" : undefined,
              color: this.props.messagesStore.hasDarkBackground ? "#fff" : undefined
            }}
            onFocus={() => this.preventIosScrollJumpOnFocus()}
            showClearTextButton
            clearTextCallback={() => {
              if (this.state.searchText) {
                this.setState({ searchText: "" }, () => this.searchMessages());
              }
            }}
          />
        </div>
      </div>
    );
  }

  private getCollapsedMenuItems(): IMessageHeaderItemConfig[] {
    let menuItems: IMessageHeaderItemConfig[] = [];

    // settings
    const isMessage = this.props.messagesStore.component === "Messages";
    const pinnedMessagesEnabled = this.props.messagesStore.instance?.settings?.enablePinMessages && this.props.messagesStore.showPinnedMessages && !this.props.messagesStore.hideSortPanelButton && !this.props.messagesStore.isFetchingMessages && this.props.messagesStore.userRole === 'Administrator';
    const collapseCommandBarItems = this.props.messagesStore.collapseCommandBarItems && isMessage;
    const showMarkAllAsRead = isMessage && this.props.messagesStore.instance.settings && this.props.messagesStore.instance.settings.enableReadUnreadMessage && this.props.messagesStore.enableMarkAllAsRead && this.props.messagesStore.messages.some((message) => !message.isRead);
    const showShowAllButton = this.props.messagesStore.showShowAllButton && !!this.props.messagesStore.showAllButtonLink;

    // items
    const pinnedMessagesItem: IMessageHeaderItemConfig = {
      enabled: pinnedMessagesEnabled,
      position: "collapsedMenu",
      config: {
        translation: this.localizationService.strings.Messages_Pinned,
        actionName: 'ShowPinnedSortPanel',
        icon: 'https://intraactivestorage.blob.core.windows.net/cdn/icons/Pin/SVG/ic_fluent_pin_48_filled.svg'
      }
    };
    const clearFilterItem: IMessageHeaderItemConfig = {
      enabled: this.props.messagesStore.filterData.isFiltered && this.props.messagesStore.showFilter,
      position: "collapsedMenu",
      config: {
        translation: this.localizationService.strings.Messages_Clear_Filter,
        actionName: 'ClearFilter',
        icon: 'ClearFilter'
      }
    };
    const showFilterItem: IMessageHeaderItemConfig = {
      enabled: this.props.messagesStore.showFilter,
      position: "collapsedMenu",
      config: {
        translation: this.localizationService.strings.Messages_Filter,
        actionName: 'Filter',
        icon: 'Filter'
      }
    };
    const markAllAsReadItem: IMessageHeaderItemConfig = {
      enabled: showMarkAllAsRead,
      position: "collapsedMenu",
      config: {
        translation: this.localizationService.strings.Messages_ReadAll,
        actionName: 'MarkAllAsRead',
        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Checkmark%20Circle/SVG/ic_fluent_checkmark_circle_48_regular.svg",
      }
    };
    const showAllButtonItem: IMessageHeaderItemConfig = {
      enabled: showShowAllButton,
      position: "collapsedMenu",
      config: {
        translation: this.localizationService.strings.Messages_ShowAll,
        actionName: 'showAll',
        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Archive/SVG/ic_fluent_archive_48_regular.svg",
      }
    };

    menuItems.push(...[
      pinnedMessagesItem,
      clearFilterItem,
      showFilterItem,
      markAllAsReadItem,
      showAllButtonItem
    ]);

    if (collapseCommandBarItems) {
      // if collapse is turned on, put everything in the menu items

      this.props.messagesStore.availableTemplates?.forEach(template => {
        menuItems.unshift({
          enabled: this.props.messagesStore.addMessage,
          position: "collapsedMenu",
          config: {
            translation: `${template.title}`,
            actionName: template.id,
            icon: 'Add'
          }
        });
      })

      menuItems.unshift({
        enabled: this.props.messagesStore.addMessage && !this.props.messagesStore.instance?.settings?.disableAddPost,
        position: "collapsedMenu",
        config: {
          translation: `${this.localizationService.strings.Messages_Add} ${(this.localizationService.strings.Messages_AddNewsLink ?? "").toLocaleLowerCase()}`,
          actionName: 'AddNewsLink',
          icon: 'Add'
        }
      });

      menuItems.unshift({
        enabled: this.props.messagesStore.addMessage && !this.props.messagesStore.instance?.settings?.disableAddNewsLink,
        position: "collapsedMenu",
        config: {
          translation: `${this.localizationService.strings.Messages_Add} ${(this.localizationService.strings.Messages_AddMessage ?? "").toLocaleLowerCase()}`,
          actionName: 'AddNewMessage',
          icon: 'Add'
        }
      });
    } else {
      // if collapse is turned off, check how much room there is for the remaining icons
      let iconsAllowedInHeader: number;
      const width = this.props.messagesStore.width;
      if (width < 200) {
        iconsAllowedInHeader = 0;
      } else if (200 <= width && width < 350) {
        iconsAllowedInHeader = 1;
      } else if (350 <= width && width < 550) {
        iconsAllowedInHeader = 2;
      } else if (550 <= width && width < 750) {
        iconsAllowedInHeader = 4;
      } else {
        iconsAllowedInHeader = 10;
      }

      let iconsAddedToHeader = 0;
      menuItems.forEach((item) => {
        if (iconsAddedToHeader < iconsAllowedInHeader && item.enabled) {
          item.position = "header";
          iconsAddedToHeader++;
        }
      });
    }

    return menuItems;
  }

  public render(): JSX.Element {
    let pagingString: string;

    if (this.props.messagesStore.startDate) {
      switch (this.props.messagesStore.showPeriod) {
        case 'day':
          pagingString = this.dateTimeService.getUserfriendlyDate(this.props.messagesStore.startDate);
          break;
        case 'week':
          if (this.props.messagesStore.startDate <= new Date() && this.props.messagesStore.endDate >= new Date()) {
            pagingString = this.localizationService.strings.Messages_ThisWeek;
          } else {
            pagingString =
              this.dateTimeService.ConvertToDDMM(this.props.messagesStore.startDate) +
              ' - ' +
              this.dateTimeService.ConvertToDDMM(this.props.messagesStore.endDate);
          }
          break;
        case 'month':
          pagingString =
            this.dateTimeService.getNameOfMonth(this.props.messagesStore.startDate) +
            ' ' +
            this.props.messagesStore.startDate.getFullYear();
          break;
      }
    }

    const showMarkAllAsRead = this.props.messagesStore.component === 'Messages' && this.props.messagesStore.instance.settings && this.props.messagesStore.instance.settings.enableReadUnreadMessage && this.props.messagesStore.enableMarkAllAsRead && this.props.messagesStore.messages.some((message) => !message.isRead);
    const showShowAllButton = this.props.messagesStore.showShowAllButton && this.props.messagesStore.showAllButtonLink && this.props.messagesStore.display !== "Calendar";
    const showAddMessage = this.props.messagesStore.addMessage && this.props.messagesStore.userRole != 'Reader' && (!this.props.messagesStore.instance?.settings?.disableAddPost || !this.props.messagesStore.instance?.settings?.disableAddNewsLink || this.props.messagesStore.availableTemplates?.length > 0);
    const showDropDownIcon = this.getAmountOfTemplates() > 1;
    const pinnedMessagesEnabled = this.props.messagesStore.instance?.settings?.enablePinMessages && this.props.messagesStore.showPinnedMessages && !this.props.messagesStore.hideSortPanelButton && !this.props.messagesStore.isFetchingMessages && this.props.messagesStore.userRole === 'Administrator';

    const eventShowsDatePaging = this.props.messagesStore.display !== "Calendar" &&
      this.props.messagesStore.showPeriod != 'fullView' &&
      this.props.messagesStore.showPeriod != 'upcomming' &&
      !this.props.messagesStore.filterData.isFiltered &&
      this.props.messagesStore.component === 'Event' &&
      this.props.messagesStore.showPaging &&
      !this.props.messagesStore.isAdminPage;

    const showFilterButton = this.props.messagesStore.showFilter;

    const headerHasItems = showAddMessage || this.props.messagesStore.showFilter || this.props.messagesStore.showSearch || this.props.messagesStore.addMessage || eventShowsDatePaging || showMarkAllAsRead || pinnedMessagesEnabled || showShowAllButton || showFilterButton;

    const collapseCommandBarItems = this.props.messagesStore.collapseCommandBarItems && this.props.messagesStore.component === 'Messages';

    let collapsedMenuItems: SmallMenuItem[] = this.getCollapsedMenuItems()
      .filter(item => item.enabled && item.position === "collapsedMenu")
      .map(item => item.config);

    return (
      <div
        style={{
          color: this.props.messagesStore.hasDarkBackground ? "#ffffff" : "#333333",
          position: 'relative',
          clear: "both"
        }}
      >
        {this.props.messagesStore.width != undefined &&
          <FilterPanel
            open={this.props.messagesStore.showFilterPanel != undefined && this.props.messagesStore.showFilterPanel}
            reloadMessages={() => {
              this.props.messagesStore.messages = [];
              this.props.messagesStore.pageNumber = 1;
              this.props.messagesStore.loading = true;
              this.props.getMessages();
            }}
            clearFiltersKey={this.state.clearFiltersKey} />
        }
        <div className={styles.IA_headerTitle}>
          {collapseCommandBarItems && this.props.messagesStore.customTitleElement
            ? this.props.messagesStore.customTitleElement
            : (this.props.messagesStore.header || this.props.close) &&
            <div style={{ marginBottom: this.props.messagesStore?.isNavigator ? 11 : 2, minWidth: 0, flex: "1 1 auto" }}>
              <IAEditableTitle
                show={this.props.messagesStore.header}
                isEditMode={this.props.messagesStore.webpartDisplayMode === 2}
                title={this.props.messagesStore.title}
                permalinkId={this.props.messagesStore.component === "Event" ? "Events" : "Messages"}
                headLineColor={this.props.messagesStore.headLineColor}
                hasDarkBackground={this.props.messagesStore.hasDarkBackground}
                headlineFontSize={this.props.messagesStore.headlineFontSize}
                headlineFontStyle={this.props.messagesStore.headlineFontStyle}
                headlineTextAlign={this.props.messagesStore.headlineTextAlign}
                headlineFontWeight={this.props.messagesStore.headlineFontWeight}
                headlineTextDecoration={this.props.messagesStore.headlineTextDecoration}
                headlineLineHeight={this.props.messagesStore.headlineLineHeight}
                headlineTag={this.props.messagesStore.headlineTag}
                themeColors={this.props.messagesStore.themeColors}
                fontFamily={this.props.messagesStore.fontSlots?.heading?.fontFamily}
                onChange={(changes) => this.props.onTitleSettingsChange?.(changes)}
              />
            </div>
          }
          {collapseCommandBarItems && collapsedMenuItems.length > 0 &&
            <IASmallMenu
              menuItems={collapsedMenuItems}
              isOverlayed={this.props.messagesStore.preview}
              highlightColor={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
              iconColor={Helper.isLightColor(this.props.messagesStore.highlightColor) ? "#333333" : this.props.messagesStore.highlightColor}
              onMenuItemClick={this.onMenuItemClick.bind(this)}
              styles={{ marginLeft: "auto", marginBottom: 11 }}
            />
          }
        </div>
        {
          this.props.close != undefined && (
            <IAIcon
              url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Dismiss/SVG/ic_fluent_dismiss_48_regular.svg"
              size={18}
              color="#777777"
              onClick={() => this.props.close()}
              style={{
                cursor: 'pointer',
                marginLeft: 10,
                right: 20,
                position: 'absolute',
                top: 20,
              }}
            />
          )
        }
        {!collapseCommandBarItems && headerHasItems &&
          <div
            className={styles.IA_header}
            style={{
              height: this.props.messagesStore.isMobile ? 52 : 32,
              margin: this.props.messagesStore.isMobile ? "0px auto 15px" : "0px auto 5px",
            }}
          >
            {showAddMessage && this.props.messagesStore.instance &&
              <div
                ref={ref => this.dropDownRef = ref}
                style={{
                  position: "relative"
                }}
              >
                <IAButton
                  label={this.localizationService.strings.Messages_Add}
                  buttonColor="transparent"
                  textColor={this.props.messagesStore.hasDarkBackground ? "#ffffff" : "#333333"}
                  icon="Add"
                  height={22}
                  iconColor={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                  fontSize={14}
                  disbaled={this.props.messagesStore.isFetchingMessages || this.props.messagesStore.userRole == undefined}
                  dataAutomationId="messages-add-new"
                  style={{
                    opacity: this.props.messagesStore.preview || this.props.messagesStore.isFetchingMessages || this.props.messagesStore.userRole == undefined ? 0.3 : 1,
                    cursor: this.props.messagesStore.preview ? "default" : "pointer",
                    padding: "0 0 0 5px"
                  }}
                  onClick={() => this.addButtonClicked()}
                />
                {showDropDownIcon &&
                  <IAButton
                    buttonColor="transparent"
                    iconUrl={this.state.showAddMessageDropDown
                      ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron Up/SVG/ic_fluent_chevron_up_48_regular.svg"
                      : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron Down/SVG/ic_fluent_chevron_down_48_regular.svg"}
                    height={22}
                    iconColor={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                    fontSize={14}
                    disbaled={this.props.messagesStore.isFetchingMessages || this.props.messagesStore.userRole == undefined}
                    style={{
                      opacity: this.props.messagesStore.preview || this.props.messagesStore.isFetchingMessages || this.props.messagesStore.userRole == undefined ? 0.3 : 1,
                      cursor: this.props.messagesStore.preview ? "default" : "pointer",
                      paddingRight: 10
                    }}
                    onClick={() => this.setState({ showAddMessageDropDown: !this.state.showAddMessageDropDown })}
                  />
                }
                {this.state.showAddMessageDropDown &&
                  <IAAddMessageDropdown
                    addNewMessage={(isNewsLink, jsonTemplate, templateName) => {
                      this.setState({ showAddMessageDropDown: false });
                      this.addNewMessage(isNewsLink, jsonTemplate, templateName);
                    }}
                    hideDropdown={() => this.setState({ showAddMessageDropDown: false })}
                    containerRef={this.dropDownRef}
                    availableTemplates={this.props.messagesStore.availableTemplates}
                    disableAddPost={this.props.messagesStore.instance?.settings?.disableAddPost || (this.props.messagesStore.component !== "Messages" && this.props.messagesStore.component !== "Event")}
                    disableAddNewsLink={this.props.messagesStore.instance?.settings?.disableAddNewsLink}
                    isEvents={this.props.messagesStore.component === "Event"}
                  />
                }
              </div>
            }
            {pinnedMessagesEnabled && this.props.messagesStore.width >= 350 && this.props.messagesStore.instance &&
              <IAButton
                label={this.localizationService.strings.Messages_Pinned}
                buttonColor="transparent"
                textColor={this.props.messagesStore.hasDarkBackground ? "#ffffff" : "#333333"}
                iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Pin/SVG/ic_fluent_pin_48_regular.svg"
                height={22}
                iconColor={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                fontSize={14}
                disbaled={this.props.messagesStore.isFetchingMessages || this.props.messagesStore.userRole == undefined}
                style={{
                  opacity: this.props.messagesStore.preview || this.props.messagesStore.isFetchingMessages || this.props.messagesStore.userRole == undefined ? 0.3 : 1,
                  cursor: this.props.messagesStore.preview ? "default" : "pointer",
                  padding: "0 10px 0 5px"
                }}
                onClick={() => {
                  this.props.messagesStore.showSortPinnedPanel = true;
                }}
              />
            }
            {
              <div
                className={styles.IA_headerRight}
                style={{
                  height: this.props.messagesStore.isMobile ? 40 : 22,
                  paddingRight: this.props.messagesStore.isMobile ? 0 : 3
                }}
              >
                {eventShowsDatePaging &&
                  (<>
                    <IAIcon
                      url="https://intraactivestorage.blob.core.windows.net/cdn/icons/iOS%20Arrow%20LTR/SVG/ic_fluent_ios_arrow_ltr_24_regular.svg"
                      size={18}
                      color={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                      onClick={() => {
                        if (this.props.messagesStore.isFetchingMessages) {
                          return;
                        }
                        this.props.messagesStore.getCalendarPage(this.dateTimeService, "prev");
                        this.props.getMessages();
                      }}
                      style={{
                        cursor: 'pointer',
                        marginTop: "3px",
                        opacity: this.props.messagesStore.isFetchingMessages ? 0.3 : 1
                      }}
                    />
                    <div
                      style={{
                        width: 'auto',
                        paddingLeft: 10,
                        paddingRight: 10,
                        textAlign: 'center',
                        fontSize: 14,
                        lineHeight: '16px',
                        marginTop: "3px",
                        color: this.props.messagesStore.hasDarkBackground ? "#ffffff" : "#333333"
                      }}
                    >
                      {pagingString}
                    </div>
                    <IAIcon
                      url="https://intraactivestorage.blob.core.windows.net/cdn/icons/iOS%20Arrow%20RTL/SVG/ic_fluent_ios_arrow_rtl_24_regular.svg"
                      size={18}
                      color={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                      onClick={() => {
                        if (this.props.messagesStore.isFetchingMessages) {
                          return;
                        }
                        this.props.messagesStore.getCalendarPage(this.dateTimeService, "next");
                        this.props.getMessages();
                      }}
                      style={{
                        cursor: 'pointer',
                        marginTop: "3px",
                        opacity: this.props.messagesStore.isFetchingMessages ? 0.3 : 1
                      }}
                    />
                  </>
                  )}
                {this.props.messagesStore.component === 'Messages' && this.props.messagesStore.showSearch &&
                  this.getSearchField(this.props.messagesStore.isMobile && !(showAddMessage && showFilterButton))
                }
                {showFilterButton &&
                  <div className={styles.IA_filterBtnWrapper} style={{ alignItems: 'center' }}>
                    {this.props.messagesStore.filterData.isFiltered && collapsedMenuItems.every(collapsedMenuItems => collapsedMenuItems.actionName !== 'ClearFilter') &&
                      <div
                        className={styles.IA_filterBtn}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <div
                          className="IA_OnFocus"
                          tabIndex={0}
                          onClick={() => this.clearFilters()}
                          style={{ marginRight: 10, display: 'flex', alignItems: 'center' }}
                          onKeyDown={(event) => {
                            if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
                              this.clearFilters();
                            }
                          }}
                        >
                          <IAIcon
                            url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Filter%20Dismiss/SVG/ic_fluent_filter_dismiss_24_regular.svg"
                            size={18}
                            color={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                            style={{
                              marginTop: 2,
                              marginRight: 5,
                            }}
                          />
                          {this.localizationService.strings.Messages_Clear_Filter}
                        </div>
                        {
                          collapsedMenuItems.every(collapsedMenuItems => collapsedMenuItems.actionName !== 'Filter') && <div className={styles.IA_filterSpace}> | </div>
                        }
                      </div>
                    }
                    {collapsedMenuItems.every(collapsedMenuItems => collapsedMenuItems.actionName !== 'Filter') &&
                      <div
                        className={[styles.IA_filterBtn, "IA_OnFocus"].join(" ")}
                        tabIndex={0}
                        style={{
                          opacity: this.props.messagesStore.preview ? 0.3 : 1,
                          cursor: this.props.messagesStore.preview ? "default" : "pointer",
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onClick={() => this.toggleFiltering()}
                        onKeyDown={(event) => {
                          if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
                            this.toggleFiltering();
                          }
                        }}
                      >
                        <IAIcon
                          url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Filter/SVG/ic_fluent_filter_28_regular.svg"
                          size={18}
                          color={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                          style={{
                            marginTop: 2,
                            marginRight: 5,
                          }}
                        />
                        {this.localizationService.strings.Messages_Filter}
                      </div>
                    }
                  </div>
                }
                {showMarkAllAsRead && collapsedMenuItems.every(collapsedMenuItems => collapsedMenuItems.actionName !== 'MarkAllAsRead') &&
                  <div
                    className={[styles.IA_filterBtn, "IA_OnFocus"].join(" ")}
                    tabIndex={0}
                    style={{
                      opacity: this.props.messagesStore.preview ? 0.3 : 1,
                      cursor: this.props.messagesStore.preview ? "default" : "pointer",
                      display: 'flex',
                      marginLeft: 12
                    }}
                    onClick={() => this.markAllAsRead()}
                    onKeyDown={(event) => {
                      if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
                        this.markAllAsRead();
                      }
                    }}
                  >
                    <IAIcon
                      url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Checkmark%20Circle/SVG/ic_fluent_checkmark_circle_48_regular.svg"
                      size={18}
                      color={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                      style={{
                        marginTop: 2,
                        marginRight: 5,
                      }}
                    />
                    <span>
                      {this.localizationService.strings.Messages_ReadAll}
                    </span>
                  </div>
                }
                {showShowAllButton && collapsedMenuItems.every(collapsedMenuItems => collapsedMenuItems.actionName !== 'showAll') &&
                  <a
                    className={styles.IA_showAllButton}
                    href={this.props.messagesStore.showAllButtonLink}
                  >
                    <IAIcon
                      url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Archive/SVG/ic_fluent_archive_48_regular.svg"
                      size={18}
                      color={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                      style={{
                        marginTop: 2,
                        marginRight: 5,
                      }}
                    />
                    {this.localizationService.strings.Messages_ShowAll}
                  </a>
                }
                {collapsedMenuItems.length > 0 &&
                  <IASmallMenu
                    menuItems={collapsedMenuItems}
                    isOverlayed={this.props.messagesStore.preview}
                    highlightColor={this.props.messagesStore.hasDarkBackground ? "#ffffff" : this.props.messagesStore.highlightColor}
                    iconColor={Helper.isLightColor(this.props.messagesStore.highlightColor) ? "#333333" : this.props.messagesStore.highlightColor}
                    onMenuItemClick={this.onMenuItemClick.bind(this)}
                  />}
              </div>
            }
          </div>
        }
      </div >
    );
  }
}

export interface IMessageHeaderItemConfig {
  enabled: boolean;
  position: IMessageHeaderItemPosition;
  config: SmallMenuItem;
}
export type IMessageHeaderItemPosition = "header" | "collapsedMenu";
export type IMessageType = "message" | "newsLink" | "template";